/**
 * ConnectionsPanel Component
 * 
 * Displays household connections with their accounts in a hierarchical view.
 * Supports: Akahu, Manual, CSV, Plaid
 * 
 * Features:
 * - Shows connection status (active, inactive, error, expired)
 * - Expandable cards showing accounts with balances
 * - Last sync time display
 * - Disconnect with confirmation dialog
 * - Reconnect prompts for broken connections
 */

import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Building2,
  Edit3,
  FileSpreadsheet,
  Globe,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Unlink,
  Plus,
  Wallet,
  CreditCard,
  PiggyBank,
  TrendingUp,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';
import {
  fetchConnections,
  fetchConnectionAccounts,
  disconnectConnection,
  getProviderInfo,
  getStatusInfo
} from '../lib/connections';
import DisconnectConfirmationDialog from './DisconnectConfirmationDialog';
import ConnectionStatusAlert from './ConnectionStatusAlert';

// Account type icons
const ACCOUNT_ICONS = {
  checking: Wallet,
  savings: PiggyBank,
  credit: CreditCard,
  loan: TrendingUp,
  investment: TrendingUp,
  cash: Wallet,
  default: Wallet
};

// Provider icons
const PROVIDER_ICONS = {
  manual: Edit3,
  akahu: Building2,
  plaid: Globe,
  csv: FileSpreadsheet
};

function ConnectionCard({ connection, onDisconnect, onSync, onReconnect, syncing }) {
  const [expanded, setExpanded] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  
  const providerInfo = getProviderInfo(connection.provider);
  const statusInfo = getStatusInfo(connection.status);
  const ProviderIcon = PROVIDER_ICONS[connection.provider] || Building2;
  
  // Check if connection has issues
  const hasIssue = connection.status === 'inactive' || 
                   connection.status === 'expired' || 
                   connection.status === 'error' ||
                   connection.last_sync_error;
  
  const loadAccounts = async () => {
    if (accounts.length > 0) return;
    setLoadingAccounts(true);
    try {
      const data = await fetchConnectionAccounts(connection.connection_id);
      console.log('[ConnectionsPanel] Accounts API Response:', {
        connectionId: connection.connection_id,
        type: typeof data,
        keys: typeof data === 'object' ? Object.keys(data) : 'N/A',
        accountsCount: data.accounts?.length ?? 'no accounts key',
        accounts: data.accounts
      });
      setAccounts(data.accounts || []);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };
  
  const handleToggle = () => {
    if (!expanded) {
      loadAccounts();
    }
    setExpanded(!expanded);
  };
  
  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectConnection(connection.connection_id);
      toast.success('Bank disconnected successfully');
      setShowDisconnectDialog(false);
      onDisconnect?.(connection.connection_id);
    } catch (error) {
      toast.error('Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };
  
  // Format balance
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };
  
  // Format last sync time
  const formatLastSync = (dateStr) => {
    if (!dateStr) return 'Never synced';
    const date = new Date(dateStr);
    return date.toLocaleString('en-NZ', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Calculate total balance
  const totalBalance = accounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
  
  // Status badge styling
  const getStatusBadgeClass = () => {
    switch (connection.status) {
      case 'active':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800';
      case 'inactive':
      case 'expired':
        return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800';
      case 'error':
        return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
    }
  };
  
  return (
    <>
      <div 
        className={`border rounded-xl overflow-hidden bg-card ${hasIssue ? 'border-amber-300 dark:border-amber-700' : ''}`}
        data-testid={`connection-card-${connection.connection_id}`}
      >
        {/* Connection issue alert */}
        {hasIssue && connection.provider !== 'manual' && (
          <div className="px-4 pt-3">
            <ConnectionStatusAlert 
              status={connection.status === 'active' && connection.last_sync_error ? 'error' : connection.status}
              compact={true}
              onReconnect={onReconnect}
              showReconnect={!!onReconnect}
            />
          </div>
        )}
        
        {/* Header */}
        <div 
          className="p-4 flex items-center justify-between cursor-pointer hover:bg-muted/30 transition-colors"
          onClick={handleToggle}
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              connection.provider === 'akahu' ? 'bg-teal-100 dark:bg-teal-900/30' :
              connection.provider === 'manual' ? 'bg-slate-100 dark:bg-slate-800' :
              'bg-blue-100 dark:bg-blue-900/30'
            }`}>
              <ProviderIcon className={`w-5 h-5 ${
                connection.provider === 'akahu' ? 'text-teal-600 dark:text-teal-400' :
                connection.provider === 'manual' ? 'text-slate-600 dark:text-slate-400' :
                'text-blue-600 dark:text-blue-400'
              }`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-foreground">{connection.display_name}</h4>
                <Badge variant="outline" className={`text-xs ${getStatusBadgeClass()}`}>
                  {statusInfo.label}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {connection.accounts_count} account{connection.accounts_count !== 1 ? 's' : ''}
                {accounts.length > 0 && ` • ${formatCurrency(totalBalance)}`}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {connection.provider !== 'manual' && statusInfo.canSync && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onSync?.(connection);
                }}
                disabled={syncing}
                className="text-muted-foreground"
                title="Sync now"
              >
                {syncing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
            )}
            {expanded ? (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
        </div>
      
      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-border">
          {loadingAccounts ? (
            <div className="p-4 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Loading accounts...
            </div>
          ) : accounts.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground text-sm">
              No accounts in this connection
            </div>
          ) : (
            <div className="divide-y divide-border">
              {accounts.map((account) => {
                const AccountIcon = ACCOUNT_ICONS[account.account_type] || ACCOUNT_ICONS.default;
                const isAccountActive = account.status !== 'inactive';
                return (
                  <div 
                    key={account.account_id}
                    className={`p-3 px-4 flex items-center justify-between hover:bg-muted/20 ${!isAccountActive ? 'opacity-60' : ''}`}
                    data-testid={`account-row-${account.account_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <AccountIcon className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground">{account.name}</p>
                          {!isAccountActive && (
                            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {account.institution || account.account_type}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${account.balance >= 0 ? 'text-foreground' : 'text-red-600 dark:text-red-400'}`}>
                        {formatCurrency(account.balance)}
                      </p>
                      {account.last_sync_at && (
                        <p className="text-xs text-muted-foreground">
                          Synced {formatLastSync(account.last_sync_at)}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Connection details & actions */}
          <div className="p-3 bg-muted/20 border-t border-border">
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">
                <span>{connection.provider === 'manual' ? 'Manually entered data' : `Connected via ${providerInfo.name}`}</span>
                {connection.last_synced_at && connection.provider !== 'manual' && (
                  <span className="ml-2 flex items-center gap-1 inline-flex">
                    <Clock className="w-3 h-3" />
                    Last sync: {formatLastSync(connection.last_synced_at)}
                  </span>
                )}
              </div>
              {connection.provider !== 'manual' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDisconnectDialog(true);
                  }}
                  className="text-muted-foreground hover:text-destructive text-xs"
                  data-testid={`disconnect-btn-${connection.connection_id}`}
                >
                  <Unlink className="w-3 h-3 mr-1" />
                  Disconnect
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    
    {/* Disconnect Confirmation Dialog */}
    <DisconnectConfirmationDialog
      open={showDisconnectDialog}
      onOpenChange={setShowDisconnectDialog}
      onConfirm={handleDisconnect}
      connectionName={connection.display_name}
      loading={disconnecting}
    />
    </>
  );
}

export default function ConnectionsPanel({ onAddAccount, onSync, onReconnect }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingConnection, setSyncingConnection] = useState(null);
  
  const loadConnections = async () => {
    try {
      const data = await fetchConnections();
      console.log('[ConnectionsPanel] Connections API Response:', {
        type: typeof data,
        keys: typeof data === 'object' ? Object.keys(data) : 'N/A',
        connectionsCount: data.connections?.length ?? 'no connections key',
        connections: data.connections?.map(c => ({ 
          id: c.connection_id, 
          name: c.display_name, 
          accounts_count: c.accounts_count 
        }))
      });
      setConnections(data.connections || []);
    } catch (error) {
      console.error('Failed to load connections:', error);
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadConnections();
  }, []);
  
  const handleDisconnect = (connectionId) => {
    setConnections(prev => prev.filter(c => c.connection_id !== connectionId));
  };
  
  const handleSync = async (connection) => {
    setSyncingConnection(connection.connection_id);
    try {
      await onSync?.(connection);
    } finally {
      setSyncingConnection(null);
      loadConnections(); // Refresh to get updated counts
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading connections...
      </div>
    );
  }
  
  // Group connections by type
  const manualConnections = connections.filter(c => c.provider === 'manual');
  const bankConnections = connections.filter(c => c.provider === 'akahu' || c.provider === 'plaid');
  const importConnections = connections.filter(c => c.provider === 'csv');
  
  // Check for any unhealthy bank connections
  const unhealthyBankConnections = bankConnections.filter(c => 
    c.status === 'inactive' || c.status === 'expired' || c.status === 'error' || c.last_sync_error
  );
  
  const hasNoConnections = connections.length === 0;
  
  return (
    <div className="space-y-6" data-testid="connections-panel">
      {/* Global connection issues alert */}
      {unhealthyBankConnections.length > 0 && (
        <ConnectionStatusAlert
          status={unhealthyBankConnections[0].status === 'active' ? 'error' : unhealthyBankConnections[0].status}
          connectionName={unhealthyBankConnections.length === 1 ? unhealthyBankConnections[0].display_name : `${unhealthyBankConnections.length} connections`}
          onReconnect={onReconnect}
          showReconnect={!!onReconnect}
        />
      )}
      
      {/* Empty State */}
      {hasNoConnections && (
        <div className="text-center py-12 px-6 border-2 border-dashed rounded-xl">
          <Building2 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No accounts connected</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Connect your bank or add accounts manually to get started.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button onClick={onAddAccount} variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Add Manual Account
            </Button>
          </div>
        </div>
      )}
      
      {/* Bank Connections */}
      {bankConnections.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Bank Connections
          </h3>
          <div className="space-y-3">
            {bankConnections.map((conn) => (
              <ConnectionCard
                key={conn.connection_id}
                connection={conn}
                onDisconnect={handleDisconnect}
                onSync={handleSync}
                onReconnect={onReconnect}
                syncing={syncingConnection === conn.connection_id}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Manual Accounts */}
      {manualConnections.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
            <Edit3 className="w-4 h-4" />
            Manual Accounts
          </h3>
          <div className="space-y-3">
            {manualConnections.map((conn) => (
              <ConnectionCard
                key={conn.connection_id}
                connection={conn}
                onDisconnect={handleDisconnect}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* CSV Imports */}
      {importConnections.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4" />
            CSV Imports
          </h3>
          <div className="space-y-3">
            {importConnections.map((conn) => (
              <ConnectionCard
                key={conn.connection_id}
                connection={conn}
                onDisconnect={handleDisconnect}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Add Account Button */}
      {connections.length > 0 && (
        <Button
          onClick={onAddAccount}
          variant="outline"
          className="w-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Manual Account
        </Button>
      )}
    </div>
  );
}
